import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import { MILE_RANGES } from '../constants/filterConstants';
import FacetSection from './FacetSection/FacetSection';
import DistanceFilter from './DistanceFilter/DistanceFilter';
import MakeFacetGroup from './MakeFacetGroup/MakeFacetGroup';
import '../global-sass/main.scss';

const FACET_CONFIG = [
	{
		id: 'distance',
		label: 'DISTANCE',
		component: DistanceFilter,
		getProps: ({ radius, onRadiusChange }) => ({
			currentRange: radius,
			onRangeChange: onRadiusChange
		})
	},
	{
		id: 'make',
		label: 'MAKE',
		component: MakeFacetGroup,
		conditional: ({ makes }) => makes && makes.length > 0,
		getProps: ({ makes, selectedMakes }) => ({ makes, selectedMakes })
	}
];

const FacetsPanel = ({
	radius,
	onRadiusChange,
	hasLocations,
	showFilter,
	makes,
	selectedMakes,
	className,
	isPortal
}) => {
	const labels = useLabels();
	const [shouldRender, setShouldRender] = useState(false);

	const isVisible = hasLocations && showFilter;

	useEffect(() => {
		if (isVisible) {
			setShouldRender(true);
		}
	}, [isVisible]);

	const handleTransitionEnd = () => {
		if (!isVisible) {
			setShouldRender(false);
		}
	};

	if (!hasLocations) return null;

	const allProps = { radius, onRadiusChange, makes, selectedMakes };

	const panelClassName = setClassNames([
		'locations-facets',
		isVisible && !isPortal && 'facets-panel--visible',
		isPortal && 'facets-panel--portal',
		isVisible && 'px-3',
		className
	]);

	return (
		<div
			className={panelClassName}
			data-testid="facets-panel"
			onTransitionEnd={isPortal ? undefined : handleTransitionEnd}
		>
			{shouldRender &&
				FACET_CONFIG.filter(
					({ conditional }) => !conditional || conditional(allProps)
				).map(
					(
						{ id, label, component: FacetComponent, getProps },
						index
					) => (
						<FacetSection
							key={id}
							title={labels.get(label) || label}
							defaultExpanded
							className={index > 0 ? 'pt-5' : ''}
						>
							<FacetComponent {...getProps(allProps)} />
						</FacetSection>
					)
				)}
		</div>
	);
};

FacetsPanel.propTypes = {
	radius: PropTypes.oneOf([...MILE_RANGES]).isRequired,
	onRadiusChange: PropTypes.func.isRequired,
	hasLocations: PropTypes.bool.isRequired,
	showFilter: PropTypes.bool.isRequired,
	makes: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			count: PropTypes.number.isRequired
		})
	),
	selectedMakes: PropTypes.arrayOf(PropTypes.string),
	className: PropTypes.string,
	isPortal: PropTypes.bool
};

FacetsPanel.defaultProps = {
	makes: [],
	selectedMakes: [],
	className: '',
	isPortal: false
};

export default FacetsPanel;
